<template>
  <div class="add-to-basket">
    <button class="btn btn-sm btn-accent-1"
            :disabled="disabled"
            @click="ctaHandler">
      {{ buttonText }}
    </button>
    <ConfirmModal
      :show="showBuyNowConfirmModal"
      heading="Add to basket?"
      @onConfirm="onBuyNow"
      @onClose="showBuyNowConfirmModal = false">
      <span>{{ confirmationText }}</span>
    </ConfirmModal>
    <ConfirmModal
      :show="showErrorDialogue"
      heading="Error adding products"
      cancel-text="Okay"
      :show-confirm-button="false"
      @onClose="showErrorDialogue = false">
      <!-- disallowed -->
      <div v-if="errors.products.disallowed.length > 0">
        <p>
          The following products could not be added to the basket because you don't have the
          relevant permissions to do so.
        </p>
        <ul>
          <li v-for="(item, index) in errors.products.disallowed"
              :key="index">
            {{ item.name }}
          </li>
        </ul>
      </div>
      <br>
      <!-- fully booked -->
      <div v-if="errors.products.fullyBooked.length > 0">
        <p>
          The following products are fully booked and could not be added to the basket.
        </p>
        <ul>
          <li v-for="(item, index) in errors.products.fullyBooked"
              :key="index">
            {{ item.name }}
          </li>
        </ul>
      </div>
      <br>
      <!-- unavailable -->
      <div v-if="errors.products.unavailable.length > 0">
        <p>
          The following products can no longer be added to the basket as an associated mandatory
          course is fully booked.
        </p>
        <ul>
          <li v-for="(item, index) in errors.products.unavailable"
              :key="index">
            {{ item.name }}
          </li>
        </ul>
      </div>
      <br>
      <!-- failed -->
      <div v-if="errors.products.failed.length > 0">
        <p>
          The following products failed to be added to the basket.
        </p>
        <ul>
          <li v-for="(item, index) in errors.products.failed"
              :key="index">
            {{ item.name }}
          </li>
        </ul>
      </div>
    </ConfirmModal>
  </div>
</template>

<script>
// import { BasketResource } from "spiritweb.packages.ecommerce";
import ConfirmModal from "~/Components/ConfirmModal.vue";
// import { addToBasket } from "../../Scripts/resources/wishlistResource";
// import { getInstance/* , startBooking */ } from "~/Scripts/resources/eventsResource";
import { getBasketId } from "~/Scripts/resources/basketStepResource";
import { transferOrderLines, transferOrderLineModel } from "~/Scripts/resources/ordersResource";

// Duplicated from ecommerce @/constants.js
// todo: export/import from that package instead
const basket = {
  addToBasket: {
    REQUEST: "sw::addToBasket::request",
    RESPONSE: "sw::addToBasket::response",
    SUCCESS: "sw::addToBasket::success",
    FAILURE: "sw::addToBasket::failure"
  }
};

export default {
  name: "MoveToBasket",
  components: {
    ConfirmModal
  },
  props: {
    wishListId: {
      type: String,
      required: true
    },
    wishListProducts: {
      type: Array,
      default: () => []
    },
    buttonText: {
      type: String,
      default: "Add to basket?"
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      showBuyNowConfirmModal: false,
      showErrorDialogue: false,
      errors: {
        products: {
          disallowed: [],
          failed: [],
          fullyBooked: [],
          unavailable: []
        }
      }
    };
  },
  computed: {
    confirmationText () {
      const items = this.wishListProducts.length === 1 ? "this wish list item" : "these wish list items";

      return `Are you sure you want to add ${items} to your basket?`;
    }
  },
  created () {
    getBasketId().then( result => {
      const { data: basketId } = result;
      this.basketId = basketId;
    } );
  },
  methods: {
    ctaHandler () {
      this.showBuyNowConfirmModal = true;
    },
    async onBuyNow () {
      this.showBuyNowConfirmModal = false;

      if ( !this.wishListId ) {
        console.warn( `No wishlist ID found: ${this.wishListId}` );
        return;
      }
      this.$emit( "loading", true );
      this.resetProductTransferErrors();

      this.onRequest( this.wishListProducts );

      this.transferProductToBasket( this.wishListProducts )
        .then( result => {
          const { data } = result;
          this.handleTransferResponse( data );
        } )
        .catch( err => {
          console.error( "Failed transfer: ", err );
          this.onFail( new Error( err ) );
        } )
        .finally( () => {
          this.$emit( "loading", false );
        } );
    },
    resetProductTransferErrors () {
      this.errors.products.disallowed = [];
      this.errors.products.failed = [];
      this.errors.products.unavailable = [];
      this.errors.products.fullyBooked = [];
    },
    getProduct ( id ) {
      return this.wishListProducts.find( p => p.productId === id ) || {};
    },
    handleTransferResponse ( response ) {
      const successfulProducts = [];

      const products = response.map( transferResult => {
        const {
          bookingTransferResult,
          // removedOrderLineId,
          orderLineTransferResult: {
            errorCode,
            errorDescription,
            // orderLineId,
            productId
          }
        } = transferResult || {
          orderLineTransferResult: {}
        };

        const { productDisplayName } = this.getProduct( productId );

        switch ( errorCode ) {
          case 108:
          case 111: {
            this.showErrorDialogue = true;
            this.errors.products.fullyBooked.push( {
              productId,
              name: productDisplayName
            } );
            const { fullyBooked } = bookingTransferResult;
            this.onFail( new Error( "This event is now fully booked" ), errorCode, fullyBooked );
            break;
          }
          case 121: {
            this.showErrorDialogue = true;
            this.errors.products.unavailable.push( {
              productId,
              name: productDisplayName
            } );
            this.onFail( new Error( "An accompanying mandatory session is full" ), errorCode, true );
            break;
          }
          case 0:
          case 200: {
            successfulProducts.push( transferResult );
            // this.onSuccess( result );
            break;
          }
          case 101:
          case 103: // can't modify a completed wishlit
          case 401: {
            this.showErrorDialogue = true;
            this.errors.products.disallowed.push( {
              productId,
              name: productDisplayName
            } );
            break;
          }
          case 500:
          default: {
            this.showErrorDialogue = true;
            this.errors.products.failed.push( {
              productId,
              name: productDisplayName
            } );
            const error = `Handle status code: ${errorCode}: ${errorDescription}`;
            console.warn( error );
            this.onFail( new Error( error ), errorCode, false );
            break;
          }
        }

        return productId;
      } );

      // set failed products
      let failedProducts = [];
      Object.keys( this.errors.products ).forEach( key => {
        failedProducts = failedProducts.concat( this.errors.products[key] );
      } );

      if ( failedProducts.length > 0 ) {
        console.log( "The following products failed to transfer: ", failedProducts );
      }

      if ( successfulProducts.length > 0 ) {
        this.onSuccess( successfulProducts );
      }

      this.onResponse( products );
    },
    async transferProductToBasket ( products ) {
      // if the basket id has not been defined, get the basket id
      if ( !this.basketId ) {
        const { data: basketId } = ( await getBasketId() ) || {};
        this.basketId = basketId;
      }

      // copy when adding multiple/all products otherwise remove
      const hasMultiple = products.length > 1;

      const transferableOrderLines = products.map( product => {
        // TODO: if product has order line transfer as copy: product.id, "NO_BOOKING", true
        console.log( product );
        return transferOrderLineModel( product.id, "STARTED", hasMultiple );
      } );

      return new Promise( ( resolve, reject ) => {
        transferOrderLines( this.wishListId, this.basketId, transferableOrderLines )
          .then( res => resolve( res ) )
          .catch( ex => reject( ex ) );
      } );
    },
    onFail ( error, code = null, showUser = false ) {
      this.$root.$emit( basket.addToBasket.FAILURE, error, code, showUser );
      this.$emit( "failure", error, code, showUser );
    },
    onSuccess ( payload ) {
      this.$root.$emit( basket.addToBasket.SUCCESS, payload );
      this.$emit( "success", payload );
    },
    onResponse ( product ) {
      this.$root.$emit( basket.addToBasket.RESPONSE, product );
      this.$emit( "response", product );
      this.requesting = false;
    },
    onRequest ( product ) {
      this.$root.$emit( basket.addToBasket.REQUEST, product );
      this.$emit( "request" );
      this.requesting = true;
    }
  }
};
</script>

<style lang="less" scoped>
.add-to-basket {
  display: inline-block;
}
</style>
